import withGS from '../core/withGShoc';
import TripleViewer from '../core/TripleViewer';
import HistoryViewer from '../core/HistoryViewer';
import Grid from '@material-ui/core/Grid';
import ResourceSummaryDetail from '../ontology/ResourceSummaryDetail';
import DataOptions from '../core/DataOptions';
import Notification from '../core/Notification';
import GS from '../grafsync/grafsync';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import AssociatedProjectDisplay from '../application-core/AssociatedProjectDisplay';
import { ENTITLEMENTS } from '../application-core/constants';
import PropTypes from 'prop-types';
import React from 'react';
import PrimaryButton from '../core/PrimaryButton';
import { globalStyles } from '../core/style/gstheme';

const styles = Object.assign({}, globalStyles);

class TemplateDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            view: 0
        };
    }

    changeView = (index) => this.setState({ view: index });

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    onActualUpdate = (property, lang, index, propertyGraphIRI) => (value) => {
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);
    };

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) =>
        this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    includeExistingResource = (property, propertyGraphIRI) => (newIRI) => {
        // We load the concept we are about to use to make sure rendering works okay
        // But if it's external it won't load and that's okay
        GS.loadData([newIRI], {}, this.props.graph).then(() => {
            GS.addProperty(this.props.id, this.props.graph, property, newIRI, null, null, propertyGraphIRI);
        });
    };

    render() {

        const { classes, hideDialog, ontologyProjectAssociation, data, graph, defaultLang, projectIRI, componentIRI,
            isReadOnly, viewLang, projectLang, projectType, propertyTemplates, components, linkedData, title,
            associatedTaxonomyProject, resourceClasses, requiredEntitlement, id } = this.props;
        const { view } = this.state;

        return (
            <Dialog
                data-test="template-dialog"
                maxWidth="xl"
                fullScreen
                className={classes.dialog}
                classes={{
                    paper: classes.paper,
                    paperWidthXl: classes.xLargeDialog
                }}
                open={true}
                onClose={() => hideDialog(false)}
                aria-labelledby="template-dialog-title">
                <DialogTitle disableTypography className={classes.mainHeading} id="template-dialog-title">{title}</DialogTitle>
                <DialogContent>
                    <div style={{ display: 'flex' }}>
                        <div style={{ marginTop: '8px' }}>
                            <DataOptions
                                handleNotAvailable={true}
                                useWorkflow={false}
                                historyOption={true}
                                changeView={this.changeView}
                                view={view}
                                id={data.id}
                                graph={graph}
                            />
                        </div>
                        <div style={{ width: 'calc(100% - 60px)', paddingLeft: '10px' }}>
                            <ResourceSummaryDetail
                                handleNotAvailable={true}
                                ariaLabel="Dialog resource summary detail"
                                project={projectIRI}
                                suppressLabel={true}
                                useWorkflow={false}
                                allowIRIchange={false}
                                canDelete={false}
                                isReadOnly={isReadOnly}
                                graph={graph}
                                defaultLang={defaultLang}
                                id={id}
                                viewLang={viewLang}
                                permission={requiredEntitlement} />
                            {data.pendingDeletion &&
                                <Notification error={true} text="This annotation is pending deletion." dataTest="pending-deletion" />
                            }
                            {data.deprecated &&
                                <Notification text="This annotation is deprecated." dataTest="deprecated" />
                            }
                            {view === 0 && ontologyProjectAssociation &&
                                <Grid container spacing={1}>
                                    {[...ontologyProjectAssociation].sort().map((opa) =>
                                        <AssociatedProjectDisplay
                                            resourceData={data}
                                            graph={graph}
                                            forceBaseClass={true}
                                            baseInferredClass="https://graphifi.com/schema/xkos/Note"
                                            projectIRI={projectIRI}
                                            schemeIRI={componentIRI}
                                            useWorkflow={false}
                                            reify={false}
                                            global={false}
                                            suppress={
                                                [
                                                    "Symmetric",
                                                    "Inverse"
                                                ]
                                            }
                                            propertyTemplates={propertyTemplates}
                                            isReadOnly={isReadOnly}
                                            viewLang={viewLang}
                                            projectLang={projectLang}
                                            projectType={projectType}
                                            linkedData={linkedData}
                                            informEmpty={true}
                                            associatedTaxonomyProject={associatedTaxonomyProject}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            includeExistingResource={this.includeExistingResource}
                                            components={components}
                                            resourceClasses={resourceClasses}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onActualUpdate}
                                            key={opa}
                                            id={opa}
                                            defaultLang={defaultLang} />
                                    )}
                                </Grid>
                            }
                            {view === 1 &&
                                <TripleViewer
                                    handleNotAvailable={true}
                                    requiredEntitlement={ENTITLEMENTS.EDIT_TAXONOMY}
                                    isReadOnly={isReadOnly}
                                    useWorkflow={false}
                                    id={id}
                                    graph={graph} />}
                            {view === 4 &&
                                <HistoryViewer
                                    handleNotAvailable={true}
                                    id={id}
                                    graph={graph} />}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <PrimaryButton dataTest="close-template-dialog" onClick={() => hideDialog(false)} >Close</PrimaryButton>
                </DialogActions>
            </Dialog>
        );
    }
}

TemplateDialog.defaultProps = {
    title: "Detail"
};

TemplateDialog.propTypes = {
    projectIRI: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    title: PropTypes.string,
    id: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    projectType: PropTypes.string.isRequired,
    projectLang: PropTypes.array.isRequired,
    viewLang: PropTypes.string.isRequired,
    associatedTaxonomyProject: PropTypes.array,
    ontologyProjectAssociation: PropTypes.array,
    requiredEntitlement: PropTypes.string.isRequired,
    hideDialog: PropTypes.func,
    components: PropTypes.array,
    propertyTemplates: PropTypes.array,
    resourceClasses: PropTypes.array,
    linkedData: PropTypes.array,
    data: PropTypes.object.isRequired,
    componentIRI: PropTypes.string,
    isReadOnly: PropTypes.bool,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(TemplateDialog));